import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import UnsavedChangesDialog from "@/components/UnsavedChangesDialog";
import { setPromptLeaveHandler } from "@/lib/unsavedChangesRegistry";

interface UnsavedChangesContextValue {
  requestLeave: (onConfirm: () => void) => void;
}

const UnsavedChangesContext = createContext<UnsavedChangesContextValue | null>(
  null,
);

export function UnsavedChangesProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const pendingActionRef = useRef<(() => void) | null>(null);
  const confirmingRef = useRef(false);

  const requestLeave = useCallback((onConfirm: () => void) => {
    pendingActionRef.current = onConfirm;
    setOpen(true);
  }, []);

  useEffect(() => {
    setPromptLeaveHandler(requestLeave);
    return () => setPromptLeaveHandler(null);
  }, [requestLeave]);

  const handleConfirm = () => {
    confirmingRef.current = true;
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    setOpen(false);
    try {
      action?.();
    } finally {
      confirmingRef.current = false;
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setOpen(true);
      return;
    }
    setOpen(false);
    if (!confirmingRef.current) {
      pendingActionRef.current = null;
    }
  };

  return (
    <UnsavedChangesContext.Provider value={{ requestLeave }}>
      {children}
      <UnsavedChangesDialog
        open={open}
        onOpenChange={handleOpenChange}
        onConfirm={handleConfirm}
      />
    </UnsavedChangesContext.Provider>
  );
}

export function useUnsavedChangesContext(): UnsavedChangesContextValue {
  const ctx = useContext(UnsavedChangesContext);
  if (!ctx) {
    throw new Error(
      "useUnsavedChangesContext must be used within UnsavedChangesProvider",
    );
  }
  return ctx;
}
